import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { DynamicForm } from '@shadcn/components/dynamic-form';
import { SqlEditor } from '@shadcn/components/sql-editor';
import { useSheetNavigation } from '~/lib/context/sheetNavigation';
import { useRunQuery } from '@shadcn/hooks/use-run-query';
import { useListTables } from '@shadcn/hooks/use-tables';
import { z, type ZodType } from 'zod';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Skeleton } from '~/components/ui/Skeleton';
import type { PostgresTable } from '@shadcn/lib/pg-meta/types';
import type { SortDir } from '~/components/ui/SortIndicator';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

const DEFAULT_LIMIT = 100;

const getDefaultSql = (name: string, sortField?: string, sortDirection?: SortDir) => {
  if (!sortDirection || !sortField) {
    return `SELECT * FROM public."${name}" LIMIT ${DEFAULT_LIMIT};`;
  } else {
    return `SELECT * FROM public."${name}" ORDER BY ${sortField} ${sortDirection} LIMIT ${DEFAULT_LIMIT};`;
  }
};

// Helper to generate a Zod schema from the table's column definitions
function generateZodSchema(table: any): z.ZodObject<any, any> {
  if (!table || !table.columns) {
    return z.object({});
  }

  const shape: Record<string, ZodType> = {};

  for (const column of table.columns) {
    // We can only edit updatable columns that are not generated
    if (!column.is_updatable || column.is_generated) {
      continue;
    }

    let fieldSchema: ZodType;

    const dataType = column.data_type.toLowerCase();

    if (dataType.includes('array')) {
      fieldSchema = z.array(z.any());
    } else if (dataType.includes('int') || dataType.includes('numeric')) {
      fieldSchema = z.number();
    } else if (dataType.includes('bool')) {
      fieldSchema = z.boolean();
    } else if (dataType === 'user-defined' && column.enums && Array.isArray(column.enums) && column.enums.length > 0) {
      // Handle enum types
      fieldSchema = z.enum(column.enums as [string, ...string[]]);
    } else {
      // Default to string for text, varchar, timestamp, uuid, etc.
      fieldSchema = z.string();
    }

    if (column.is_nullable) {
      fieldSchema = fieldSchema.nullish();
    }

    shape[column.name] = fieldSchema;
  }

  return z.object(shape);
}

const getPrimaryKeys = (table: PostgresTable): string[] => {
  if (!table || !table.primary_keys) {
    return [];
  }

  return table.primary_keys.map((pk) => pk.name);
};

function EditRowView({
  table,
  row,
  onSuccess,
  onCancel,
}: {
  table: PostgresTable;
  row: any;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { mutate: runUpdateQuery, isPending: isUpdatePending } = useRunQuery();
  const formSchema = useMemo(() => generateZodSchema(table), [table]);

  const columnInfo = useMemo(() => {
    if (!table || !table.columns) {
      return {};
    }

    const info: Record<string, any> = {};

    for (const column of table.columns) {
      // Only include updatable columns that are not generated
      if (!column.is_updatable || column.is_generated) {
        continue;
      }

      const dataType = column.data_type.toLowerCase();

      const displayType =
        dataType === 'user-defined' && column.enums && Array.isArray(column.enums) && column.enums.length > 0
          ? 'enum'
          : dataType;

      info[column.name] = {
        data_type: displayType,
        is_nullable: column.is_nullable,
      };
    }

    return info;
  }, [table]);

  const handleFormSubmit = useCallback(
    (formData: any) => {
      const pks = getPrimaryKeys(table);

      if (pks.length === 0) {
        toast.error('Cannot update row. This table does not have a primary key.');
        return;
      }

      const setClauses = Object.entries(formData)
        .map(([key, value]) => {
          if (JSON.stringify(row[key]) === JSON.stringify(value)) {
            return null;
          }

          // Find the column type to determine formatting
          const column = table.columns?.find((col: any) => col.name === key);
          const dataType = column?.data_type?.toLowerCase() || '';
          const isNullable = column?.is_nullable || false;

          let formattedValue;

          // Handle empty/null values
          if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            if (isNullable) {
              formattedValue = 'NULL';
            } else if (dataType.includes('array')) {
              // Non-nullable array, set to empty array
              const jsonObj = JSON.stringify({ [key]: [] });
              formattedValue = `(select ${key} from json_populate_record(null::public."${
                table.name
              }", '${jsonObj.replace(/'/g, "''")}'))`;
            } else {
              // Non-nullable text field, set to empty string
              formattedValue = "''";
            }
          } else if (Array.isArray(value) && value.length === 0) {
            // Explicitly empty array (different from null)
            const jsonObj = JSON.stringify({ [key]: [] });
            formattedValue = `(select ${key} from json_populate_record(null::public."${
              table.name
            }", '${jsonObj.replace(/'/g, "''")}'))`;
          } else if (dataType.includes('array')) {
            // Array type with actual values - use json_populate_record syntax
            const jsonObj = JSON.stringify({ [key]: value });
            formattedValue = `(select ${key} from json_populate_record(null::public."${
              table.name
            }", '${jsonObj.replace(/'/g, "''")}'))`;
          } else if (dataType === 'user-defined' && column?.enums) {
            // Handle enum values - treat as strings with proper escaping
            formattedValue = `'${String(value).replace(/'/g, "''")}'`;
          } else if (typeof value === 'string') {
            formattedValue = `'${value.replace(/'/g, "''")}'`;
          } else {
            formattedValue = value;
          }

          return `"${key}" = ${formattedValue}`;
        })
        .filter(Boolean)
        .join(', ');

      if (!setClauses) {
        toast.error('No changes to save');
        onSuccess();

        return;
      }

      const whereClauses = pks
        .map((pk) => {
          const value = row[pk];
          const formattedValue = typeof value === 'string' ? `'${value.replace(/'/g, "''")}'` : value;

          return `"${pk}" = ${formattedValue}`;
        })
        .join(' AND ');

      const updateSql = `UPDATE public."${table.name}" SET ${setClauses} WHERE ${whereClauses};`;

      runUpdateQuery({ query: updateSql }, { onSuccess });
    },
    [table, row, runUpdateQuery, onSuccess],
  );

  return (
    <div className="px-6 pt-2 lg:px-8">
      <h2 className="text-lg font-semibold my-2">Editing record</h2>
      <DynamicForm
        schema={formSchema}
        initialValues={row}
        onSubmit={handleFormSubmit}
        onCancel={onCancel}
        isLoading={isUpdatePending}
        columnInfo={columnInfo}
      />
    </div>
  );
}

function TableRecordsView({ table, onQuery }: { table: PostgresTable; onQuery: () => void }) {
  const { push, pop } = useSheetNavigation();
  const [refetchCounter, setRefetchCounter] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDir>(undefined);

  const handleRowClick = useCallback(
    (row: any) => {
      push({
        title: `Editing record`,
        component: (
          <EditRowView
            table={table}
            row={row}
            onCancel={() => {
              pop();
            }}
            onSuccess={() => {
              pop();
              setRefetchCounter((c) => c + 1);
            }}
          />
        ),
      });
    },
    [push, pop, table],
  );

  const handleQuery = () => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.DatabaseDataQueried,
      },
    });
    onQuery();
  };

  return (
    <div className="px-6 lg:px-8">
      <SqlEditor
        label={
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-base text-bolt-ds-textPrimary font-semibold">Records for {table.name}</h2>
            <div className="flex gap-4">
              <Button
                variant="secondary"
                startIcon="i-heroicons:arrow-path size-4 mb-0.5"
                onClick={() => {
                  setRefetchCounter((c) => c + 1);
                }}
                size="md"
              >
                Refresh
              </Button>
              <Button
                variant="secondary"
                onClick={handleQuery}
                size="md"
                startIcon="i-heroicons:magnifying-glass size-4 mb-0.5"
              >
                Query
              </Button>
            </div>
          </div>
        }
        initialSql={getDefaultSql(table.name, sortField, sortDirection)}
        hideSql
        onSortChange={(sortField, sortDirection) => {
          setSortField(sortField);
          setSortDirection(sortDirection);
        }}
        defaultSortDirection={sortDirection}
        defaultSortField={sortField}
        defaultLimit={DEFAULT_LIMIT}
        queryKey={table.id}
        onRowClick={handleRowClick}
        runAutomatically={true}
        readOnly={true}
        refetch={refetchCounter}
      />
    </div>
  );
}

export function DatabaseManager() {
  const { push } = useSheetNavigation();
  const { data: tables, isLoading, isError } = useListTables(['public']);

  const handleTableClick = useCallback(
    (table: PostgresTable) => {
      push({
        title: table.name,
        component: (
          <TableRecordsView
            table={table}
            onQuery={() => {
              handleNaturalLanguageQueryClick(table);
            }}
          />
        ),
      });
    },
    [push],
  );

  const handleNaturalLanguageQueryClick = (table?: PostgresTable) => {
    push({
      title: 'Query',
      component: (
        <div className="px-6 lg:px-8">
          <SqlEditor
            label={
              <div className="flex flex-col gap-6">
                <div className="text-base text-bolt-ds-textPrimary font-semibold">
                  Query {table ? table.name : 'your database'}
                </div>
                <div className="text-sm text-bolt-ds-textSecondary">Your query</div>
              </div>
            }
            initialSql={table ? getDefaultSql(table.name) : undefined}
            defaultLimit={DEFAULT_LIMIT}
          />
        </div>
      ),
    });
  };

  return (
    <div className="p-6 pt-4 lg:p-8 lg:pt-8">
      <div className="flex items-center justify-between mb-6 gap-6">
        <div className="flex-1">
          <h1 className="text-base lg:text-xl font-semibold">Tables</h1>
          <p className="hidden lg:block text-sm lg:text-base text-bolt-ds-textSecondary mt-2">
            View and manage the data stored in your app.
          </p>
        </div>
      </div>

      {isError && (
        <Alert type="danger" title="Error loading tables">
          There was a problem loading your database tables.
        </Alert>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {tables && tables.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {tables.map((table: any) => (
            <Button
              variant="secondary"
              key={table.id}
              truncate={false}
              size="lg"
              className="overflow-hidden flex justify-start gap-2 hover:bg-bolt-ds-surfaceHighlight bg-bolt-ds-surface border border-bolt-ds-borderOutline px-4 py-3"
              onClick={() => handleTableClick(table)}
            >
              <div className="flex justify-between w-full items-center gap-1">
                <div className="flex gap-2 items-center truncate max-w-4/5">
                  <div className="i-heroicons:table-cells size-5 text-bolt-ds-textTertiary mb-0.5" />
                  <div className="text-sm font-medium truncate">{table.name}</div>
                </div>
                <div className="text-xs text-bolt-ds-textTertiary text-nowrap">{table.live_rows_estimate} rows</div>
              </div>
            </Button>
          ))}
        </div>
      ) : !isLoading && !isError && (!tables || tables.length === 0) ? (
        <div className="text-bolt-ds-textTertiary">You don't have any tables yet.</div>
      ) : null}
    </div>
  );
}
